"use client";

import * as React from "react";
import { ArrowRight, ClipboardList, FileText, Briefcase, Loader, House as Home, Check, Info, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const services = [{
  value: "Business & Corporate",
  title: "事業・会社関連",
  description: "法人設立、契約書、事業売買、会社運営、所有権に関する問題など、フロリダ州でのビジネスに関する法的事項についてご相談いただけます。",
  icon: Briefcase
}, {
  value: "Real Estate",
  title: "不動産関連",
  description: "不動産取引、売買、所有権、契約、クロージングに関する問題など、フロリダ州の不動産に関する法的事項についてご相談いただけます。",
  icon: Home
}, {
  value: "Estate Planning & Probate",
  title: "遺言・信託・プロベート関連",
  description: "遺言書が関係するプロベート、信託管理、受益者または相続人に関する問題、遺産財産、債権者請求、Personal RepresentativeまたはTrusteeの職務、関連する裁判手続についてご相談いただけます。当事務所では、遺言書の新規作成、Estate Planning、委任状、医療指示書その他の生前対策書類の作成は取り扱っていません。",
  icon: ClipboardList
}, {
  value: "Attorney Fee Hearings",
  title: "弁護士費用・Attorney’s Fee Hearings",
  description: "弁護士費用の妥当性が争点となる案件において、Attorney’s Fee Hearings に関する専門家証人サービスを提供しています。",
  icon: FileText
}];
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [selectedService, setSelectedService] = React.useState<string>("");
  if (isSubmitted && !submitError) {
    return <div className="max-w-2xl mx-auto text-center py-20 animate-in fade-in zoom-in-95 duration-700">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-lg bg-secondary/10 mb-8">
          <Check className="size-10 text-secondary" />
        </div>
        <h3 className="font-serif text-4xl text-foreground mb-4">初期連絡を送信しました</h3>
        <p className="text-muted-foreground mb-10 max-w-md mx-auto leading-relaxed text-lg">お問い合わせをいただきありがとうございます。内容を確認し、通常1〜2営業日以内に担当者より日本語または英語でご連絡いたします。</p>
        <Button onClick={resetForm} variant="outline" className="px-10 h-12 rounded-lg border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-all">
          別のお問い合わせを送信する
        </Button>
      </div>;
  }
  return <div className="max-w-4xl mx-auto">
      <div className="mb-12 animate-in fade-in slide-in-from-top-4 duration-500">
        <p className="text-foreground text-lg leading-relaxed">
          このフォームは、当事務所の弁護士が内容を確認するための初期情報整理を目的としています。わかる範囲でご入力ください。未確定の項目は空欄でも構いません。
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="w-full space-y-24">
        {}
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4 tracking-tight">1. 相談内容を選択してください</h2>
            <p className="text-muted-foreground text-lg">最も近いものを1つお選びください。迷う場合は、最後の入力欄で詳しく説明できます。</p>
          </div>

          <FormField control={control} name="service_type" rules={{
          required: "分野を選択してください"
        }} render={({
          field
        }) => <FormItem>
                <div className="grid grid-cols-1 gap-5">
                  {services.map((service, i) => {
              const Icon = service.icon;
              const isSelected = selectedService === service.value;
              return <div data-index={i} key={service.value} onClick={() => {
                setSelectedService(service.value);
                field.onChange(service.value);
              }} className={["cursor-pointer p-8 transition-all duration-300 border rounded-lg group", isSelected ? "border-secondary bg-secondary/5 shadow-md ring-1 ring-secondary/30" : "border-border bg-card hover:border-secondary/40 hover:shadow-sm"].join(" ")}>
                        <div className="flex items-start gap-8">
                          <div className={`shrink-0 p-4 rounded-lg transition-colors duration-300 ${isSelected ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground group-hover:bg-secondary/10 group-hover:text-secondary"}`}>
                            <Icon className="size-8" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-serif text-2xl mb-2 transition-colors ${isSelected ? "text-secondary" : "text-foreground"}`}>{service.title}</h3>
                            <p className="text-muted-foreground text-base leading-relaxed max-w-3xl">{service.description}</p>
                          </div>
                          {isSelected && <div className="shrink-0 self-center">
                              <div className="size-8 rounded-lg bg-secondary flex items-center justify-center">
                                <Check className="size-5 text-secondary-foreground" />
                              </div>
                            </div>}
                        </div>
                      </div>;
            })}
                </div>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="pt-24 border-t border-border animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
          <div className="mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4 tracking-tight">2. お客様情報</h2>
            <p className="text-muted-foreground text-lg">当事務所から確認の連絡をするための基本情報です。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            <FormField control={control} name="full_name" rules={{
            required: "氏名は必須です"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">氏名</FormLabel>
                  <FormControl>
                    <Input placeholder="山田 太郎" {...field} className="h-14 text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "メールアドレスは必須です"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">メールアドレス</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="example@mail.com" {...field} className="h-14 text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "電話番号は必須です"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">電話番号</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="090-0000-0000" {...field} className="h-14 text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="country_residence" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">居住国・州</FormLabel>
                  <FormControl>
                    <Input placeholder="例: 日本、東京都 または USA, Florida" {...field} className="h-14 text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="matter_description" rules={{
            required: "案件の概要を入力してください"
          }} render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">案件の概要</FormLabel>
                  <FormControl>
                    <Textarea placeholder="ご相談内容の概要を記入してください..." {...field} className="min-h-[160px] text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card p-4" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="pt-24 border-t border-border animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
          <div className="mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4 tracking-tight">3. 弁護士確認に必要な情報</h2>
            <p className="text-muted-foreground text-lg">相手方、期限、対象となる州・郡、資料の有無など、弁護士が確認するために必要な情報を整理します。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            <FormField control={control} name="related_parties" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">関係者名</FormLabel>
                  <FormControl>
                    <Textarea placeholder="相手方や関連する人物・企業名" {...field} className="min-h-[100px] text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card p-4" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="important_dates" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">重要な日付（期限など）</FormLabel>
                  <FormControl>
                    <Textarea placeholder="裁判の期日や契約の期限など" {...field} className="min-h-[100px] text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card p-4" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="property_location" render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">対象となる不動産・会社・資産の所在地</FormLabel>
                  <FormControl>
                    <Textarea placeholder="フロリダ州内の住所や郡（County）など" {...field} className="min-h-[100px] text-lg border-border/80 focus-visible:border-secondary rounded-lg bg-card p-4" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="pt-24 border-t border-border animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
          <div className="mb-10">
            <h2 className="font-serif text-3xl md:text-4xl text-primary mb-4 tracking-tight">4. ご希望の連絡方法</h2>
            <p className="text-muted-foreground text-lg">希望する連絡方法と時間帯をお選びください。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            <FormField control={control} name="preferred_contact_method" rules={{
            required: "希望する連絡方法を選択してください"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">希望する連絡方法</FormLabel>
                  <FormControl>
                    <select {...field} className="w-full h-14 text-lg border border-border/80 focus:border-secondary rounded-lg bg-card px-4 transition-all appearance-none cursor-pointer outline-none">
                      <option value="">選択してください</option>
                      <option value="email">メール</option>
                      <option value="phone">電話</option>
                      <option value="zoom_teams">ビデオ会議 (Zoom/Teams)</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="preferred_contact_time" rules={{
            required: "希望する連絡時間帯を選択してください"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-bold text-sm tracking-wide uppercase">希望する連絡時間帯</FormLabel>
                  <FormControl>
                    <select {...field} className="w-full h-14 text-lg border border-border/80 focus:border-secondary rounded-lg bg-card px-4 transition-all appearance-none cursor-pointer outline-none">
                      <option value="">選択してください</option>
                      <option value="morning">午前 (EST 9:00 - 12:00)</option>
                      <option value="afternoon">午後 (EST 13:00 - 17:00)</option>
                      <option value="anytime">いつでも可</option>
                      <option value="japan_morning">日本時間の午前中</option>
                      <option value="japan_evening">日本時間の夜間</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <div className="pt-12 border-t border-border animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500">
          {submitError && <div className="mb-8 p-5 border border-destructive/40 bg-destructive/5 rounded-lg animate-in fade-in slide-in-from-top-2">
              <p className="text-destructive text-sm font-medium flex items-center gap-2">
                <span className="size-1.5 rounded-lg bg-destructive" />
                {submitError}
              </p>
            </div>}

          <div className="mb-8 p-6 bg-muted/30 rounded-lg border border-border/50">
            <p className="text-muted-foreground text-sm leading-relaxed">
              免責事項：このフォームの送信により、弁護士と依頼人の関係が成立することはありません。正式な委任契約が締結されるまでは、機密情報の送信はお控えください。
            </p>
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-xl font-bold tracking-wide group rounded-lg bg-primary hover:bg-primary/90 transition-all shadow-lg">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-3 size-6" />
                送信中...
              </> : <>
                日本語で初期連絡を送信する
                <ArrowRight className="ml-3 size-6 transition-transform group-hover:translate-x-2" />
              </>}
          </Button>
        </div>
      </Form>
    </div>;
}
export default function Intake() {
  return <section data-section-id="4063" id="japanese-inquiry-form" className="bg-background text-foreground pt-32 pb-48 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-20 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-foreground tracking-tight">日本語問い合わせフォーム</h2>
        </div>

        <FormProvider formKey="japanese_intake_form" formId="japanese_intake_form" sectionName="inquiry-form" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        country_residence: "",
        service_type: "",
        matter_description: "",
        related_parties: "",
        important_dates: "",
        property_location: "",
        preferred_contact_method: "",
        preferred_contact_time: ""
      }} validationRules={{
        full_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true
        },
        service_type: {
          required: true
        },
        matter_description: {
          required: true
        },
        preferred_contact_method: {
          required: true
        },
        preferred_contact_time: {
          required: true
        }
      }} submitText="日本語で初期連絡を送信する" successMessage="初期連絡を送信しました。内容を確認し、通常1〜2営業日以内に担当者よりご連絡いたします。" errorMessage="お問い合わせの処理中にエラーが発生しました。詳細を確認して再度お試しください。">
          <InquiryFormInner />
        </FormProvider>
      </div>
    </section>;
}